import { API_BASE } from '@/lib/api';

// 后台所有数据访问都走自建后端 /api/admin/*（替代原 Supabase Edge Functions）
// 后端会校验管理员令牌，再用服务端权限读库，前端拿不到别人的敏感数据。

const ADMIN_ACCOUNT = 'admin';
const TOKEN_KEY = 'jinan_admin_token';
const SESSION_TIME_KEY = 'jinan_admin_login_at';
// 24小时（毫秒），与后端签发的令牌有效期一致
const ADMIN_SESSION_TTL_MS = 24 * 60 * 60 * 1000;

// 云函数名 -> 自建后端接口路径
const ENDPOINTS: Record<string, string> = {
  'admin-auth': 'auth',
  'admin-dashboard': 'dashboard',
  'admin-users': 'users',
  'admin-orders': 'orders',
  'admin-notices': 'notices',
  'admin-deposits': 'deposits',
  'admin-withdrawals': 'withdrawals',
  'admin-pickups': 'pickups',
  'admin-channels': 'channels',
  'admin-trading-schedule': 'trading-schedule',
  'admin-delete-user': 'delete-user',
  'clear-all-data': 'clear-all-data',
};

function getAdminToken(): string {
  try {
    return localStorage.getItem(TOKEN_KEY) || '';
  } catch {
    return '';
  }
}

function storeAdminToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    // ignore
  }
}

interface AdminResponse<T> {
  data?: T;
  error?: string;
  detail?: string;
}

async function postAdmin<T>(path: string, body: Record<string, unknown>, token: string): Promise<AdminResponse<T>> {
  const res = await fetch(`${API_BASE}/api/admin/${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}`, 'x-admin-token': token } : {}),
    },
    body: JSON.stringify(body),
  });

  const text = await res.text();
  let parsed: AdminResponse<T> | null = null;
  if (text) {
    try {
      parsed = JSON.parse(text) as AdminResponse<T>;
    } catch {
      parsed = null;
    }
  }

  if (!res.ok) {
    if (res.status === 401) clearAdminSession();
    const message = parsed?.error || parsed?.detail || text || `请求失败(${res.status})`;
    throw new Error(message);
  }
  return parsed || {};
}

// ===== 独立后台登录（账号密码） =====
export async function checkAdminCredentials(account: string, password: string): Promise<boolean> {
  if (account !== ADMIN_ACCOUNT) return false;
  try {
    const result = await postAdmin<{ ok?: boolean; token?: string }>('auth', {
      action: 'login', account, password,
    }, '');
    const data = result.data;
    if (!data?.ok || !data.token) return false;
    storeAdminToken(data.token);
    return true;
  } catch {
    return false;
  }
}

export function isAdminLoggedIn(): boolean {
  try {
    const token = getAdminToken();
    if (!token) return false;
    const loginAt = Number(localStorage.getItem(SESSION_TIME_KEY) || '0');
    if (!loginAt) return false;
    return Date.now() - loginAt <= ADMIN_SESSION_TTL_MS;
  } catch {
    return false;
  }
}

export function setAdminSession(): void {
  try {
    localStorage.setItem(SESSION_TIME_KEY, String(Date.now()));
  } catch {
    // ignore
  }
}

export function clearAdminSession(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(SESSION_TIME_KEY);
  } catch {
    // ignore
  }
}

export interface AdminUser {
  id: string;
  phone: string;
  name: string;
  id_card: string;
  member_level: string;
  verified: boolean;
  balance: number;
  asset_value: number;
  holding_gold: number;
  order_count: number;
  role: string;
  email: string;
  created_at: string;
}

export interface AdminOrder {
  id: string;
  user_id: string;
  name: string;
  weight: number;
  direction: string;
  open_price: number;
  current_price: number;
  close_price: number | null;
  profit: number;
  original_margin: number;
  status: string;
  create_time: string;
  close_time: string | null;
  phone: string;
  user_name: string;
  lock_expire_at: string | null;
  created_at: string | null;
}

export interface Notice {
  id: number;
  tag: string;
  title: string;
  summary: string;
  date: string;
  show_on_marquee: boolean;
}

export interface DepositRecord {
  id: number;
  user_id: string;
  amount: number;
  channel: string;
  channel_id: number | null;
  channel_receiver: string | null;
  channel_bank: string | null;
  chain: string | null;
  account: string | null;
  payer_name: string | null;
  bank_name: string | null;
  status: string;
  remark: string | null;
  create_time: string | null;
  created_at: string | null;
  phone: string;
  name: string;
  screenshots: string[];
}

export interface WithdrawRecord {
  id: number;
  user_id: string;
  asset_id: string | null;
  product: string;
  weight: number;
  method: string | null;
  amount: number;
  status: string;
  create_time: string | null;
  phone: string;
  name: string;
  channel_detail: string | null;
}

export interface PickupRecord {
  id: string;
  user_id: string;
  asset_ids: string[];
  product: string;
  total_weight: number;
  method: string;
  status: string;
  address_detail: string | null;
  logistics_no: string | null;
  fee: number;
  create_time: string | null;
  created_at: string | null;
  approved_time: string | null;
  approved_by: string | null;
  remark: string | null;
  phone: string;
}

export interface DashboardStats {
  total_users: number;
  admin_users: number;
  total_orders: number;
  open_orders: number;
  pending_deposits: number;
  pending_withdrawals: number;
  pending_pickups: number;
  total_balance: number;
  total_deposit_amount: number;
  holding_gold: number;
  holding_silver: number;
  holding_kgbar: number;
  total_holding_weight: number;
  position_detail?: PositionDetail[];
}

export interface PositionDetail {
  name: string;
  buy: number;
  sell: number;
  net: number;
}

export interface UserPosition {
  id: string;
  name: string;
  weight: number;
  direction: string;
  open_price: number;
  current_price: number;
  original_margin: number;
  profit: number;
  live_profit: number;
  create_time: string;
}

export interface UserPhysicalAsset {
  id: string;
  product: string;
  weight: number;
  status: string;
  store_date: string;
  location: string;
  created_at: string;
}

export interface UserDetail {
  profile: {
    id: string;
    phone: string;
    name: string;
    email: string;
    member_level: string;
    verified: boolean;
    balance: number;
    asset_value: number;
    role: string;
    created_at: string;
  };
  positions: UserPosition[];
  physical_assets: UserPhysicalAsset[];
  summary: {
    total_position_weight: number;
    total_floating_profit: number;
    total_margin: number;
    physical_weight: number;
    net_asset_value: number;
    total_deposit_amount: number;
    total_withdraw_amount: number;
    total_profit: number;
  };
}

export interface DepositChannel {
  id: number;
  channel_type: string;
  bank_name: string | null;
  account_name: string | null;
  account_no: string | null;
  chain_type: string | null;
  remark: string | null;
  enabled: boolean;
  sort: number;
  exchange_rate: number | null;
  created_at: string | null;
}

async function adminRequest<T>(fn: string, body: Record<string, unknown> = {}): Promise<T> {
  const endpoint = ENDPOINTS[fn] || fn;
  const token = getAdminToken();
  if (!token) {
    clearAdminSession();
    throw new Error('登录已过期，请重新登录');
  }

  const result = await postAdmin<T>(endpoint, body, token);
  if (result.error) throw new Error(result.error);
  return result.data as T;
}

export const adminApi = {
  changeAdminPassword: (oldPassword: string, newPassword: string) =>
    adminRequest<{ ok: boolean }>('admin-auth', { action: 'change_password', old_password: oldPassword, new_password: newPassword }),

  dashboard: () =>
    adminRequest<DashboardStats>('admin-dashboard'),

  listUsers: () =>
    adminRequest<AdminUser[]>('admin-users', { action: 'list' }),

  clearAllData: () =>
    adminRequest<{ ok: boolean; kept_admin_profiles?: number; deleted_auth_users?: number; results?: Record<string, { success: boolean; error?: string }> }>('clear-all-data', { action: 'clear_all', confirm: 'CLEAR_ALL' }),

  getUserDetail: (userId: string) =>
    adminRequest<UserDetail>('admin-users', { action: 'get_user_detail', user_id: userId }),

  setUserRole: (userId: string, role: string) =>
    adminRequest<{ ok: boolean }>('admin-users', { action: 'set_role', user_id: userId, role }),

  deleteUser: (userId: string) =>
    adminRequest<{ ok: boolean; results?: Record<string, { success: boolean; error?: string }> }>('admin-delete-user', { user_id: userId }),

  listOrders: () =>
    adminRequest<AdminOrder[]>('admin-orders', { action: 'list' }),

  runAutoSettle: () =>
    adminRequest<{ success: boolean; settled: number; liquidated: number; total: number; prices?: number; message?: string }>('settle'),

  listNotices: () =>
    adminRequest<Notice[]>('admin-notices', { action: 'list' }),

  createNotice: (payload: { tag: string; title: string; summary: string; date: string; show_on_marquee?: boolean }) =>
    adminRequest<{ ok: boolean }>('admin-notices', { action: 'create', ...payload }),

  updateNotice: (payload: { id: number; tag: string; title: string; summary: string; date: string; show_on_marquee?: boolean }) =>
    adminRequest<{ ok: boolean }>('admin-notices', { action: 'update', ...payload }),

  deleteNotice: (id: number) =>
    adminRequest<{ ok: boolean }>('admin-notices', { action: 'delete', id }),

  listDeposits: () =>
    adminRequest<DepositRecord[]>('admin-deposits', { action: 'list' }),

  confirmDeposit: (id: number, amount?: number) =>
    adminRequest<{ ok: boolean }>('admin-deposits', { action: 'confirm', id, amount }),

  rejectDeposit: (id: number) =>
    adminRequest<{ ok: boolean }>('admin-deposits', { action: 'reject', id }),

  listWithdrawals: () =>
    adminRequest<WithdrawRecord[]>('admin-withdrawals', { action: 'list' }),

  approveWithdrawal: (id: number) =>
    adminRequest<{ ok: boolean }>('admin-withdrawals', { action: 'approve', id }),

  rejectWithdrawal: (id: number) =>
    adminRequest<{ ok: boolean }>('admin-withdrawals', { action: 'reject', id }),

  listPickups: () =>
    adminRequest<PickupRecord[]>('admin-pickups', { action: 'list' }),

  approvePickup: (id: string, opts?: { logistics_no?: string; fee?: number; remark?: string }) =>
    adminRequest<{ ok: boolean }>('admin-pickups', { action: 'approve', id, ...opts }),

  rejectPickup: (id: string, remark?: string) =>
    adminRequest<{ ok: boolean }>('admin-pickups', { action: 'reject', id, remark }),

  listChannels: () =>
    adminRequest<DepositChannel[]>('admin-channels', { action: 'list' }),

  createChannel: (payload: {
    channel_type: string;
    bank_name?: string;
    account_name: string;
    account_no: string;
    chain_type?: string;
    remark?: string;
    enabled: boolean;
    sort?: number;
    exchange_rate?: number | null;
  }) => adminRequest<{ ok: boolean }>('admin-channels', { action: 'create', ...payload }),

  updateChannel: (payload: Partial<DepositChannel> & { id: number }) =>
    adminRequest<{ ok: boolean }>('admin-channels', { action: 'update', ...payload }),

  deleteChannel: (id: number) =>
    adminRequest<{ ok: boolean }>('admin-channels', { action: 'delete', id }),

  getSchedule: () =>
    adminRequest<{ id: number; market_name: string; open_time: string; close_time: string; timezone: string; weekend_enabled: boolean; enabled: boolean; manual_paused: boolean; pause_reason: string | null; paused_categories: string[] | null; updated_at: string }>('admin-trading-schedule', { action: 'get' }),

  updateSchedule: (payload: {
    open_time?: string;
    close_time?: string;
    timezone?: string;
    weekend_enabled?: boolean;
    enabled?: boolean;
  }) => adminRequest<{ ok: boolean }>('admin-trading-schedule', { action: 'update', ...payload }),

  setPause: (paused: boolean, reason?: string) =>
    adminRequest<{ ok: boolean; paused: boolean }>('admin-trading-schedule', { action: 'set_pause', paused, reason }),

  toggleCategory: (category: string, paused: boolean) =>
    adminRequest<{ ok: boolean; category: string; paused: boolean; pausedCategories: string[] }>('admin-trading-schedule', { action: 'toggle_category', category, paused }),
};